import { Field, Formik, Form as FormikForm } from 'formik';
import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Tabs, Tab } from 'react-bootstrap';
import { CiTrash } from 'react-icons/ci';
import { FiClock, FiCalendar, FiInfo } from 'react-icons/fi';
import { createSchedule, fetchSchedulesByConversationId, deleteSchedule } from '../store/schedule/scheduleThunks';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeDots } from 'react-loader-spinner';
import * as Yup from 'yup'
import { Clock, X, Plus, Calendar, Trash2, ChevronDown } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Enhanced Date-Time Picker Component with better design
const EnhancedDateTimePicker = ({ value, onChange, minDate, maxDate, className, error }) => {
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
  const [selectedTime, setSelectedTime] = useState(value ? new Date(value) : null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  // Generate time slots (every 30 minutes)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = new Date();
        time.setHours(hour, minute, 0, 0);
        
        // Check if this time slot is valid (not in the past for today)
        const now = new Date();
        const selectedDateStart = selectedDate ? new Date(selectedDate) : new Date();
        selectedDateStart.setHours(hour, minute, 0, 0);
        
        if (selectedDate) {
          const isToday = selectedDate.toDateString() === now.toDateString();
          if (isToday && selectedDateStart <= now) {
            continue; // Skip past times for today
          }
        }
        
        slots.push({
          value: time,
          label: time.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          })
        });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setShowDatePicker(false);
    
    if (date && selectedTime) {
      const combinedDateTime = new Date(date);
      combinedDateTime.setHours(selectedTime.getHours(), selectedTime.getMinutes(), 0, 0);
      onChange(combinedDateTime);
    }
  };

  const handleTimeChange = (time) => {
    setSelectedTime(time);
    setShowTimePicker(false);
    
    if (selectedDate && time) {
      const combinedDateTime = new Date(selectedDate);
      combinedDateTime.setHours(time.getHours(), time.getMinutes(), 0, 0);
      onChange(combinedDateTime);
    }
  };

  const formatDisplayDate = (date) => {
    if (!date) return 'Select Date';
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDisplayTime = (time) => {
    if (!time) return 'Select Time';
    return time.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <div className="space-y-4">
      {/* Date Selector */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Date
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowDatePicker(!showDatePicker)}
            className={`w-full px-4 py-3 border rounded-xl bg-white text-left flex items-center justify-between transition-all duration-200 hover:shadow-md ${
              error ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-blue-400'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Calendar className="w-4 h-4 text-blue-600" />
              </div>
              <span className={selectedDate ? 'text-gray-900' : 'text-gray-500'}>
                {formatDisplayDate(selectedDate)}
              </span>
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              showDatePicker ? 'rotate-180' : ''
            }`} />
          </button>
          
          {showDatePicker && (
            <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-4">
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                minDate={minDate}
                maxDate={maxDate}
                inline
                className="custom-datepicker"
              />
            </div>
          )}
        </div>
      </div>

      {/* Time Selector */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Time
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => selectedDate && setShowTimePicker(!showTimePicker)}
            disabled={!selectedDate}
            className={`w-full px-4 py-3 border rounded-xl bg-white text-left flex items-center justify-between transition-all duration-200 hover:shadow-md ${
              !selectedDate 
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                : error 
                  ? 'border-red-300 bg-red-50' 
                  : 'border-gray-300 hover:border-blue-400'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${
                selectedDate ? 'bg-blue-50' : 'bg-gray-100'
              }`}>
                <Clock className={`w-4 h-1 ${
                  selectedDate ? 'text-blue-600' : 'text-gray-400'
                }`} />
              </div>
              <span className={
                selectedTime ? 'text-gray-900' : 
                selectedDate ? 'text-gray-500' : 'text-gray-400'
              }>
                {selectedDate ? formatDisplayTime(selectedTime) : 'Select date first'}
              </span>
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              showTimePicker ? 'rotate-180' : ''
            }`} />
          </button>
          
          {showTimePicker && selectedDate && (
            <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
              <div className="p-2">
                <div className="text-sm font-medium text-gray-700 px-3 py-2 border-b border-gray-100">
                  Available Times
                </div>
                {timeSlots.map((slot, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleTimeChange(slot.value)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors duration-150 text-sm"
                  >
                    {slot.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Combined Display */}
      {selectedDate && selectedTime && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-sm font-medium text-blue-800">
              Scheduled for: {selectedDate.toLocaleDateString('en-US', { 
                weekday: 'long',
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })} at {selectedTime.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true 
              })}
            </span>
          </div>
        </div>
      )}

      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
    </div>
  );
};

const ScheduleMessageModal = ({ show, handleClose, userName, receiverId, platform, currentChatId, hasUserMessages, lastUserMessageDate }) => {
  const [activeTab, setActiveTab] = useState('new');
  const [localScheduledMessages, setLocalScheduledMessages] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    messageText: '',
    scheduledDateTime: null,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();
  const {
    loading = false,
    error = null,
    success = false,
    conByIdLoading = false,
    conByIdError = null,
    conByIdSuccess = [],
    conDeleteLoading = false,
    conDeleteError = null,
    conDeleteSuccess = false,
  } = useSelector((state) => state?.scheduler || {});

  // Reset form when currentChatId changes
  useEffect(() => {
    if (currentChatId) {
      setFormData({
        messageText: '',
        scheduledDateTime: null,
      });
      setErrors({});
      setActiveTab('new');
    }
  }, [currentChatId]);

  // Fetch schedules when modal is opened or chat changes
  useEffect(() => {
    if (show && currentChatId) {
      dispatch(fetchSchedulesByConversationId(currentChatId));
    }
  }, [show, currentChatId, dispatch]);

  // Update localScheduledMessages when fetched schedules come
  useEffect(() => {
    if (conByIdSuccess && conByIdSuccess.length > 0) {
      setLocalScheduledMessages(conByIdSuccess);
    } else {
      setLocalScheduledMessages([]);
    }
  }, [conByIdSuccess]);

  // Update localScheduledMessages after a successful delete
  useEffect(() => {
    if (conDeleteSuccess && currentChatId) {
      dispatch(fetchSchedulesByConversationId(currentChatId));
    }
  }, [conDeleteSuccess, currentChatId, dispatch]);

  const calculateTimeLeft = (scheduledTime) => {
    const now = new Date();
    const scheduled = new Date(scheduledTime);
    const diffMs = scheduled - now;

    if (diffMs < 0) return 'Overdue';

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day(s)`;
    } else if (diffHours > 0) {
      return `${diffHours} hour(s)`;
    } else {
      return `${diffMinutes} minute(s)`;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.scheduledDateTime) {
      newErrors.scheduledDateTime = 'Date and time are required';
    } else if (formData.scheduledDateTime <= new Date()) {
      newErrors.scheduledDateTime = 'Scheduled time must be in the future';
    }

    if (!formData.messageText.trim()) {
      newErrors.messageText = 'Message text is required';
    } else if (formData.messageText.trim().length < 5) {
      newErrors.messageText = 'Message must be at least 5 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateSchedule = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        receiverId: receiverId,
        platform: platform,
        messageType: "text",
        messageText: formData.messageText.trim(),
        scheduledTime: formData.scheduledDateTime.toISOString(),
        conversationId: currentChatId
      };

      await dispatch(createSchedule(payload));

      // Add to local state for immediate UI update
      const newMessage = {
        _id: Date.now().toString(),
        ...payload,
      };
      setLocalScheduledMessages(prev => [...prev, newMessage]);

      await dispatch(fetchSchedulesByConversationId(currentChatId));

      // Reset form
      setFormData({
        messageText: '',
        scheduledDateTime: null,
      });
      setErrors({});
      setActiveTab('scheduled');
    } catch (error) {
      console.error('Error creating schedule:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLocalScheduledMessages((prev) => prev.filter((msg) => msg._id !== id));
      await dispatch(deleteSchedule(id));
    } catch (error) {
      console.error('Error deleting schedule:', error);
      // Restore the message if delete failed
      await dispatch(fetchSchedulesByConversationId(currentChatId));
    }
  };

  // Calculate min and max dates for the date picker
  const getDateConstraints = () => {
    const today = new Date();

    // If there are user messages, use the last message date as base
    let minDate = today;
    let maxDate = new Date();

    if (hasUserMessages && lastUserMessageDate) {
      minDate = new Date(lastUserMessageDate);
      maxDate = new Date(lastUserMessageDate);
      maxDate.setDate(maxDate.getDate() + 7); // 7 days from last message
    } else {
      // Default to current date and 7 days ahead
      maxDate.setDate(today.getDate() + 7);
    }

    return { minDate, maxDate };
  };

  const { minDate, maxDate } = getDateConstraints();

  // Loading dots component
  const LoadingDots = ({ size = "small", color = "white" }) => {
    const sizeClasses = size === "large" ? "w-3 h-3" : "w-2 h-2";
    const colorClasses = color === "blue" ? "bg-blue-500" : "bg-white";

    return (
      <div className="flex space-x-1 justify-center items-center">
        <div className={`${sizeClasses} ${colorClasses} rounded-full animate-bounce`} style={{ animationDelay: '0ms' }}></div>
        <div className={`${sizeClasses} ${colorClasses} rounded-full animate-bounce`} style={{ animationDelay: '150ms' }}></div>
        <div className={`${sizeClasses} ${colorClasses} rounded-full animate-bounce`} style={{ animationDelay: '300ms' }}></div>
      </div>
    );
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-opacity-50 transition-opacity duration-300">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] m-auto transform transition-all duration-500 ease-out scale-95 animate-fadeIn">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#50B5FF] to-[#4A90E2] text-white p-6 relative rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-opacity-20 rounded-xl backdrop-blur-sm">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Scheduled Messages</h2>
                <p className="text-blue-100 text-sm">Manage your message scheduling for {userName}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('new')}
              className={`flex-1 py-4 px-6 text-sm font-medium transition-all duration-200 ${activeTab === 'new'
                  ? 'text-[#50B5FF] border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Create Schedule</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('scheduled')}
              className={`flex-1 py-4 px-6 text-sm font-medium transition-all duration-200 ${activeTab === 'scheduled'
                  ? 'text-[#50B5FF] border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>Scheduled Messages</span>
                {localScheduledMessages.length > 0 && (
                  <span className="bg-[#50B5FF] text-white text-xs px-2 py-1 rounded-full">
                    {localScheduledMessages.length}
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === 'new' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                {/* Enhanced Date and Time Field */}
                <div className="mb-6">
                  <EnhancedDateTimePicker
                    value={formData.scheduledDateTime}
                    onChange={(dateTime) => setFormData(prev => ({ ...prev, scheduledDateTime: dateTime }))}
                    minDate={minDate}
                    maxDate={maxDate}
                    error={errors.scheduledDateTime}
                  />
                </div>

                {/* Message Field */}
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-3 block">Message</label>
                  <textarea
                    value={formData.messageText}
                    onChange={(e) => setFormData(prev => ({ ...prev, messageText: e.target.value }))}
                    rows="4"
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none ${errors.messageText
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400'
                      }`}
                    placeholder="Enter your message to be sent later..."
                  />
                  {errors.messageText && (
                    <p className="text-red-500 text-sm mt-2">{errors.messageText}</p>
                  )}
                  <div className="flex justify-between mt-2">
                    <p className="text-gray-500 text-xs">Minimum 5 characters required</p>
                    <p className="text-gray-500 text-xs">{formData.messageText.length}/1000</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="bg-gray-200 text-gray-700 px-8 py-3 rounded-xl font-medium hover:bg-gray-300 transition-all duration-200 hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateSchedule}
                  disabled={isSubmitting || loading}
                  className="bg-gradient-to-r from-[#50B5FF] to-[#4A90E2] text-white px-8 py-3 rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {isSubmitting || loading ? (
                    <div className="flex items-center space-x-2">
                      <LoadingDots />
                      <span>Scheduling...</span>
                    </div>
                  ) : (
                    'Schedule Message'
                  )}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'scheduled' && (
            <div>
              {conByIdLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <LoadingDots size="large" color="blue" />
                  <p className="text-gray-500 mt-4">Loading scheduled messages...</p>
                </div>
              ) : localScheduledMessages.length > 0 ? (
                <div className="space-y-4">
                  {localScheduledMessages.map((msg) => (
                    <div
                      key={msg._id}
                      className="bg-gradient-to-r from-white to-blue-50 border border-blue-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <div className="w-2 h-2 bg-[#50B5FF] rounded-full mr-3"></div>
                            <h3 className="font-semibold text-gray-800">Message to {userName}</h3>
                          </div>
                          <p className="text-gray-600 mb-3 line-clamp-3 max-w-md">
                            {msg.messageText}
                          </p>
                          <div className="flex items-center mb-2">
                            <Clock className="w-4 h-4 text-[#50B5FF] mr-2" />
                            <span className="text-[#50B5FF] font-medium text-sm">
                              Sending in {calculateTimeLeft(msg.scheduledTime)}
                            </span>
                          </div>
                          <p className="text-gray-500 text-sm">
                            {new Date(msg.scheduledTime).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDelete(msg._id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200 group"
                          title="Delete scheduled message"
                        >
                          <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-10 h-10 text-[#50B5FF]" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No scheduled messages yet</h3>
                  <p className="text-gray-500 mb-6">Create your first scheduled message to get started</p>
                  <button
                    onClick={() => setActiveTab('new')}
                    className="bg-gradient-to-r from-[#50B5FF] to-[#4A90E2] text-white px-6 py-2 rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    Create Schedule
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Animation CSS
const styles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-fadeIn {
    animation: fadeIn 0.5s ease-out forwards;
  }
  
  /* Custom DatePicker Styles */
  .custom-datepicker .react-datepicker {
    border: none;
    box-shadow: none;
    font-family: inherit;
  }
  
  .custom-datepicker .react-datepicker__header {
    background-color: #50B5FF;
    border-bottom: none;
    border-radius: 0.5rem 0.5rem 0 0;
  }
  
  .custom-datepicker .react-datepicker__current-month {
    color: white;
    font-weight: 600;
  }
  
  .custom-datepicker .react-datepicker__day-name {
    color: white;
  }
  
  .custom-datepicker .react-datepicker__day:hover {
    background-color: #50B5FF;
    color: white;
  }
  
  .custom-datepicker .react-datepicker__day--selected {
    background-color: #50B5FF;
    color: white;
  }
`;

const styleSheet = document.createElement('style');
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

export default ScheduleMessageModal;